import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { Banknote } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { type FieldValueRow, FieldValueTable } from '@/components/field-value-table';
import { CopyButton } from '@/components/ui/copy-button';
import { JsonBlock } from '@/components/ui/json-block';
import { useAmlReport } from '@/hooks/queries/funds/use-transactions';
import { useAuthStore } from '@/lib/auth/store';
import {
  buildStatusUpdateRequest,
  EDITABLE_TRANSFER_STATUSES,
  type EditableTransferStatus,
  refundTransaction,
  type Transaction,
  updateTransactionStatus,
} from '@/services/api/funds-transactions';
import { formatDate } from '@/utils/format';
import { AmlReportDialog } from './aml-report';

const DATE_FIELDS = ['user_requested_at', 'user_confirmed_at', 'completed_at'] as const;
const COPYABLE_FIELDS = ['transaction_id', 'source_account', 'destination_account', 'account_holder_id', 'account_id'] as const;

const isDateField = (field: string): boolean => (DATE_FIELDS as readonly string[]).includes(field);
const isCopyableField = (field: string): boolean => (COPYABLE_FIELDS as readonly string[]).includes(field);

const PlainValue = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>;
  if (typeof value === 'object') return <JsonBlock className="max-h-40 p-2" value={value} />;
  return <span className="break-all">{String(value)}</span>;
};
PlainValue.displayName = 'PlainValue';

interface TransactionDetailDialogProps {
  transaction: Transaction;
  onClose: () => void;
  /** Refetch the current list view after a successful mutation */
  onMutated: () => void;
}

export const TransactionDetailDialog = ({ transaction, onClose, onMutated }: TransactionDetailDialogProps) => {
  const operator = useAuthStore((state) => state.user);
  const [pendingStatus, setPendingStatus] = useState<EditableTransferStatus | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);

  // Lookup failures (other than the normal no-report 404 → null) are swallowed:
  // the dialog opens without a report either way (legacy parity).
  const reportQuery = useAmlReport(transaction);
  const report = reportQuery.data ?? null;

  // Deliberate fix: legacy attached `report` to the row object and let the
  // select mutate transfer_status in place. Dialog state stays off the row here.
  const record: Record<string, unknown> = useMemo(() => (report ? { ...transaction, report } : { ...transaction }), [transaction, report]);

  // Subaccount transfers are not editable
  const isStatusEditable = transaction.transfer_method_type !== 'SUBACCOUNT_TRANSFER';
  const canSave = pendingStatus !== null && pendingStatus !== transaction.transfer_status;

  const handleSave = async () => {
    if (!pendingStatus) return;
    // Deliberate fix: the legacy route silently no-oped without a session
    // name — block with a visible error instead of sending a broken audit payload.
    if (!operator?.name) {
      toast.error('Cannot update the status: your operator name is missing from the session. Sign in again.');
      return;
    }
    const operatorName = operator.name;
    // Deliberate fix: interpolate the pending target status, not the
    // (legacy: already-mutated) row status.
    const justification = await confirmAction({
      title: `Confirm Update Transaction Status to ${pendingStatus}`,
      description: `Proceeding with this action will update transaction ${transaction.transaction_id} to have a status of ${pendingStatus}. Please take care, if you set the status to PENDING or CONFIRMED, the funds system may or may not process the transaction again. Your action here will be logged and will be auditable.`,
      actionLabel: `Update Transaction Status to ${pendingStatus}`,
      onConfirm: (comment) =>
        updateTransactionStatus(
          buildStatusUpdateRequest({
            pid: transaction.account_holder_id,
            id: transaction.transaction_id,
            status: pendingStatus,
            comment,
            operatorName,
          }),
        ),
    });
    if (justification === null) {
      // Cancelled (including after an inline failure) — revert the select
      setPendingStatus(null);
      return;
    }
    toast.success(`Transaction status updated to ${pendingStatus}`);
    onMutated();
    onClose();
  };

  const handleRefund = async () => {
    const justification = await confirmAction({
      title: `Confirm Refund Withdrawal of ${transaction.amount} ${transaction.currency}`,
      // Exact legacy copy, including the double space and trailing wording
      description: `Proceeding with this action will return the funds from this withdrawal to the users account, (this will be done in a new transaction, this transaction will be set to FAILED).  Your action here will be auditable`,
      actionLabel: `Refund Withdrawal of ${transaction.amount} ${transaction.currency}`,
      onConfirm: async (comment) => {
        const outcome = await refundTransaction({ transactionId: transaction.transaction_id, comment });
        // A successful refund currently comes back as HTTP 500 — warn-and-refetch
        if (outcome === 'probably-succeeded') toast.warning('Refund submitted — verify the new transaction appeared');
        else toast.success('Refund submitted');
      },
    });
    if (justification === null) return;
    // Legacy cleared ALL filters here (clearAll() reuse) — just refetch the current view
    onMutated();
    onClose();
  };

  const renderValue = (field: string, value: unknown) => {
    if (field === 'report' && Array.isArray(value)) {
      return (
        <Button className="h-6 rounded-full px-3 text-xs" onClick={() => setIsReportOpen(true)} size="sm" type="button">
          AML Report
        </Button>
      );
    }
    if (isDateField(field)) return <span className="whitespace-nowrap tabular-nums">{formatDate(value as string | number | null | undefined)}</span>;
    if (field === 'transfer_status' && isStatusEditable) {
      return (
        <Select onValueChange={(next) => setPendingStatus(next as EditableTransferStatus)} value={pendingStatus ?? undefined}>
          <SelectTrigger className="w-56">
            {/* The current status can be INITIATED/CREDITED, which are not selectable targets — show it as the placeholder */}
            <SelectValue placeholder={transaction.transfer_status} />
          </SelectTrigger>
          <SelectContent>
            {EDITABLE_TRANSFER_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (isCopyableField(field) && typeof value === 'string' && value) {
      return (
        <span className="inline-flex items-center gap-1 break-all font-mono text-xs">
          {value}
          <CopyButton label={`Copy ${value}`} value={value} />
        </span>
      );
    }
    return <PlainValue value={value} />;
  };

  const rows: FieldValueRow[] = Object.entries(record).map(([field, value]) => ({ key: field, label: field, value: renderValue(field, value) }));

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="flex max-h-[90vh] w-[90vw] flex-col sm:max-w-[1000px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 break-all">
            <Banknote className="size-5 shrink-0" />
            Transfer details: {transaction.transaction_id}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
          <FieldValueTable labelVariant="code" rows={rows} />
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Close
          </Button>
          {/* Keyed on the ORIGINAL row status — an unsaved selection must not reveal/hide it */}
          {transaction.transfer_status === 'UNDER_REVIEW' ? (
            <Button onClick={handleRefund} type="button" variant="secondary">
              Refund Withdrawal
            </Button>
          ) : null}
          <Button disabled={!canSave} onClick={handleSave} type="button" variant="primary">
            Save changes
          </Button>
        </DialogFooter>

        {isReportOpen && report ? <AmlReportDialog onClose={() => setIsReportOpen(false)} report={report} /> : null}
      </DialogContent>
    </Dialog>
  );
};
TransactionDetailDialog.displayName = 'TransactionDetailDialog';
